/**
 * NotFoundPage — rota catch-all 404.
 *
 * Hero centralizado com ilustração premium (Compass + glow), tipografia
 * display, copy com personalidade ("foi pra outro lavajato"), 2 CTAs
 * (voltar pra home + ver lavagens). Sem cara de "Error 404 — page not found"
 * genérico, com cara de tela de app premium.
 */
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Compass, Sparkles, WashingMachine } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';

export function NotFoundPage() {
  return (
    <div className="bg-octa-hero relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-12">
      {/* Blobs cinematográficos */}
      <motion.div
        aria-hidden
        className="octa-blob bg-octa-gradient pointer-events-none absolute -left-32 -top-32 h-[26rem] w-[26rem]"
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 0.5, scale: 1 }}
        transition={{ duration: 1.2 }}
      />
      <motion.div
        aria-hidden
        className="octa-blob bg-octa-accent pointer-events-none absolute -bottom-32 -right-32 h-[24rem] w-[24rem]"
        style={{ animationDelay: '-4s' }}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 0.5, scale: 1 }}
        transition={{ duration: 1.2, delay: 0.15 }}
      />

      <div className="relative z-10 w-full max-w-md text-center">
        {/* Ícone hero — Compass dentro de gradient ring + glow */}
        <motion.div
          className="relative mx-auto mb-8 h-32 w-32"
          initial={{ opacity: 0, scale: 0.5, rotate: -15 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 0.7, ease: [0.34, 1.56, 0.64, 1] }}
        >
          {/* Halo blur de fundo */}
          <div
            aria-hidden
            className="bg-octa-gradient absolute inset-0 -z-10 rounded-full opacity-40 blur-3xl"
          />
          {/* Ring conic */}
          <div className="octa-conic-ring shadow-octa-glow flex h-full w-full items-center justify-center rounded-full p-[3px]">
            <div className="bg-background flex h-full w-full items-center justify-center rounded-full">
              <Compass className="text-octa-gradient h-14 w-14" strokeWidth={1.5} />
            </div>
          </div>
          {/* Sparkles decorativos */}
          <motion.div
            aria-hidden
            className="absolute -right-1 -top-1"
            initial={{ scale: 0, rotate: -45 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.5, duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
          >
            <Sparkles className="text-primary h-6 w-6 drop-shadow-md" />
          </motion.div>
        </motion.div>

        {/* "404" gigante com gradient mesh */}
        <motion.h1
          className="text-octa-mesh text-display-2xl font-extrabold tracking-tight"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          404
        </motion.h1>

        {/* Mensagem warm com personalidade */}
        <motion.h2
          className="text-display-sm mt-2 font-extrabold tracking-tight"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          Essa página foi pra outro lavajato
        </motion.h2>
        <motion.p
          className="text-muted-foreground mx-auto mt-3 max-w-xs text-sm leading-relaxed"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          O link que você acessou não existe (ou já saiu do box). Vamos te levar de volta pro lugar
          certo.
        </motion.p>

        {/* CTAs */}
        <motion.div
          className="mt-8 flex flex-col gap-2.5 sm:flex-row sm:justify-center"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.5 }}
        >
          <Link to="/" className="sm:flex-1">
            <Button variant="accent" size="lg" className="h-12 w-full rounded-xl font-bold">
              <ArrowLeft className="h-4 w-4" />
              Voltar pra home
            </Button>
          </Link>
          <Link to="/lavagens" className="sm:flex-1">
            <Button variant="outline" size="lg" className="h-12 w-full rounded-xl">
              <WashingMachine className="h-4 w-4" />
              Minhas lavagens
              <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
